import { logRuntimeError } from 'Diagnostics/runtimeLogger';
import type { ConfigMap, RuntimeConfigMap } from 'Models/uiConfig/requestTypes';
import originalConfig, { type StoreWidget } from 'Stores/p451a1-0w/config';
import { isObjectRecord } from 'Utils/guards';

// TODO: move these init helpers to a better home.

export const unprocessedConfig = { ...originalConfig };

/**
 * Reads runtime config from global namespaces and accepts only plain
 * objects. Goes through `Diagnostics/runtimeLogger` (cycle-safe
 * registry) instead of `Diagnostics/diagnostics` because this module
 * is loaded transitively during `componentlessWidgetsConfig`
 * evaluation, before `mainConfig` is exported.
 */
function readPredefinedConfig(source: unknown, sourceName: string): Record<string, unknown> {
  if (source == null) {
    return {};
  }
  if (!isObjectRecord(source)) {
    logRuntimeError({
      source: 'templates:predefinedConfig',
      error: new Error(`${sourceName} is not a plain object`),
      extra: { value: previewValue(source) },
    });
    return {};
  }
  const { config } = source as { config?: unknown };
  if (config == null) {
    return {};
  }
  if (!isObjectRecord(config)) {
    logRuntimeError({
      source: 'templates:predefinedConfig',
      error: new Error(`${sourceName}.config is not a plain object`),
      extra: { value: previewValue(config) },
    });
    return {};
  }
  return config;
}

function previewValue(value: unknown): string {
  if (value === null) {
    return 'null';
  }
  if (value === undefined) {
    return 'undefined';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return Object.prototype.toString.call(value);
  }
}

const predefinedConfig = readPredefinedConfig(window.Convermax, 'window.Convermax');
const legacyPredefinedConfig = readPredefinedConfig(window.__CONVERMAX__, 'window.__CONVERMAX__');

const runtimeConfig = {
  ...predefinedConfig,
  ...legacyPredefinedConfig,
};

type FacetTemplateConfig = RuntimeConfigMap;

type FacetTemplatesConfig = ConfigMap<FacetTemplateConfig>;

type FacetTemplateRuntimeConfig = RuntimeConfigMap & {
  templates?: FacetTemplatesConfig;
};

type RuntimeWidgetConfig = {
  Widgets?: StoreWidget[];
};

type RuntimeFacetConfig = {
  facets?: ConfigMap<FacetTemplateRuntimeConfig>;
};

type RuntimeTemplateConfig = RuntimeWidgetConfig & RuntimeFacetConfig & RuntimeConfigMap;

function extendConfig(config: RuntimeTemplateConfig): void {
  appendWidgets(config);
  appendFacets(config);
  appendSimpleKeys(config);
}

// `widget.name` is the merge key and must stay a string.
function isValidWidgetEntry(widget: unknown): widget is StoreWidget {
  return isObjectRecord(widget) && typeof (widget as { name?: unknown }).name === 'string';
}

function appendWidgets(config: RuntimeWidgetConfig): void {
  const rawSource: unknown = config.Widgets ?? [];
  if (!Array.isArray(rawSource)) {
    logRuntimeError({
      source: 'templates:appendWidgets',
      error: new Error('runtimeConfig.Widgets is not an array'),
      extra: { value: previewValue(rawSource) },
    });
    return;
  }
  const destination = unprocessedConfig.Widgets;

  rawSource.forEach((widget) => {
    if (!isValidWidgetEntry(widget)) {
      logRuntimeError({
        source: 'templates:appendWidgets:entry',
        error: new Error('runtimeConfig.Widgets entry is not a widget'),
        extra: { value: previewValue(widget) },
      });
      return;
    }
    const baseConfig = destination.find((w) => w.name === widget.name);
    if (baseConfig) {
      Object.assign(baseConfig, widget);
    } else {
      destination.push(widget);
    }
  });
}

function appendFacets({ facets: source = {} }: { facets?: ConfigMap<FacetTemplateRuntimeConfig> }): void {
  const destination = unprocessedConfig.facets as ConfigMap<FacetTemplateRuntimeConfig | undefined>;
  function getBaseFacetConfig(type: string): FacetTemplateRuntimeConfig | undefined {
    const baseConfig = destination[type];
    if (source[type].templates && baseConfig && !baseConfig.templates) {
      return { templates: { default: { ...baseConfig } } };
    }
    return baseConfig;
  }

  const copyKeys = (deepDestination: RuntimeConfigMap, deepSource: RuntimeConfigMap): void => {
    Object.entries(deepSource).forEach(([key, value]) => {
      deepDestination[key] = value;
    });
  };

  (Object.entries(source) as [string, FacetTemplateRuntimeConfig][]).forEach(([type, facetCfg]) => {
    // eslint-disable-next-line no-multi-assign
    const baseConfig = (destination[type] = getBaseFacetConfig(type));

    if (baseConfig) {
      if (facetCfg.templates) {
        const baseTemplates = baseConfig.templates as FacetTemplatesConfig;
        (Object.entries(facetCfg.templates) as [string, FacetTemplateConfig][]).forEach(
          ([name, template]) => {
            copyKeys(baseTemplates[name], template);
          },
        );
      } else {
        const base = baseConfig.templates ? (baseConfig.templates.default as RuntimeConfigMap) : baseConfig;
        copyKeys(base, facetCfg);
      }
    } else {
      destination[type] = facetCfg;
    }
  });
}

// Keys handled elsewhere instead of `appendSimpleKeys`.
const NON_SIMPLE_CONFIG_KEYS = ['Widgets', 'facets'] as const;

type NonSimpleConfigKey = (typeof NON_SIMPLE_CONFIG_KEYS)[number];

function isNonSimpleConfigKey(key: string): key is NonSimpleConfigKey {
  return (NON_SIMPLE_CONFIG_KEYS as readonly string[]).includes(key);
}

function appendSimpleKeys(config: RuntimeTemplateConfig): void {
  Object.entries(config).forEach(([key, value]) => {
    if (!isNonSimpleConfigKey(key) && value) {
      unprocessedConfig[key] = value;
    }
  });
}

if (Object.entries(runtimeConfig).length) {
  // Runtime shape is validated at the boundary and per consumer.
  extendConfig(runtimeConfig as RuntimeTemplateConfig);
}

unprocessedConfig.SearchRequestDefaults = unprocessedConfig.SearchRequestDefaults || {};
const srd = unprocessedConfig.SearchRequestDefaults;
if (!srd.extra) {
  srd.extra = {};
}

export const config = {};

if (!CONVERMAX_IS_LOCAL_ENV) {
  window.Convermax.defaultTranslations = CONVERMAX_TRANSLATIONS;
}

window.Convermax.templates = {
  config,
};
